/**
 * Custom Unlayer email blocks for the Opswarex email template editor.
 *
 * Each block is registered via `unlayer.registerTool(...)` and produces
 * HTML that includes Jinja-style merge tags ({{...}}). The backend
 * (`email_templates_module.send_test_email` and the campaign sender)
 * does a plain string replace on those tags from the recipient's
 * context at send time — abandoned-cart for cart-recovery emails,
 * order context for order-related emails, etc.
 *
 * Why a separate file: keeps `EmailTemplateEditorPage.js` readable.
 * Each tool is self-contained (icon, options, renderer); adding a new
 * one is purely additive.
 *
 * Block design rules:
 *  - Mobile-first inline CSS (no <style> tags, no flex/grid — works on
 *    Outlook 2007+).
 *  - Use 600px max-width tables for layout. Email-safe ONLY.
 *  - Brand-neutral default styling; user can re-style via Unlayer's
 *    standard property panel on each block.
 *  - Merge tags are documented in the `mergeTagsUsed` field below.
 */

// Shared merge tags that need to exist in MERGE_TAGS so Unlayer
// surfaces them in autocomplete + property-panel pickers.
export const CUSTOM_BLOCK_MERGE_TAGS = {
  // Abandoned cart
  cart_url:           { name: 'Cart URL',           value: '{{cart_url}}',           sample: 'https://example.com/cart/recover/abc' },
  cart_total:         { name: 'Cart Total',         value: '{{cart_total}}',         sample: '$249.00' },
  cart_items_count:   { name: 'Cart Items Count',   value: '{{cart_items_count}}',   sample: '3' },
  cart_items_html:    { name: 'Cart Items (HTML)',  value: '{{cart_items_html}}',    sample: '<li>Velvet Sofa × 1</li><li>Oak Coffee Table × 1</li>' },

  // Store credits / loyalty
  store_credit_balance: { name: 'Store Credit Balance', value: '{{store_credit_balance}}', sample: '$45.00' },
  store_credit_expiry:  { name: 'Store Credit Expiry',  value: '{{store_credit_expiry}}',  sample: 'Mar 15, 2026' },

  // Order details (transactional)
  order_status:       { name: 'Order Status',       value: '{{order_status}}',       sample: 'Shipped' },
  order_date:         { name: 'Order Date',         value: '{{order_date}}',         sample: 'Feb 7, 2026' },
  order_items_html:   { name: 'Order Items (HTML)', value: '{{order_items_html}}',   sample: '<li>Velvet Sofa × 1</li>' },
  order_subtotal:     { name: 'Order Subtotal',     value: '{{order_subtotal}}',     sample: '$240.00' },
  order_shipping:     { name: 'Order Shipping',     value: '{{order_shipping}}',     sample: '$9.00' },
  order_tax:          { name: 'Order Tax',          value: '{{order_tax}}',          sample: '$0.00' },
  shipping_address:   { name: 'Shipping Address',   value: '{{shipping_address}}',   sample: '12 Oak Ave, Mumbai 400001, India' },

  // Driver
  driver_name:        { name: 'Driver Name',        value: '{{driver_name}}',        sample: 'Rakesh Kumar' },
  driver_phone:       { name: 'Driver Phone',       value: '{{driver_phone}}',       sample: '+91 98765 43210' },
  driver_vehicle:     { name: 'Driver Vehicle',     value: '{{driver_vehicle}}',     sample: 'White Mahindra Bolero — MH 04 AB 1234' },
  driver_eta:         { name: 'Driver ETA',         value: '{{driver_eta}}',         sample: '2-4 PM, Feb 9' },

  // Courier
  courier_name:       { name: 'Courier Name',       value: '{{courier_name}}',       sample: 'Delhivery' },
  awb_number:         { name: 'AWB / Tracking #',   value: '{{awb_number}}',         sample: 'DLV1234567890' },
  courier_eta:        { name: 'Courier ETA',        value: '{{courier_eta}}',        sample: 'Feb 10, 2026' },
  tracking_url:       { name: 'Tracking URL',       value: '{{tracking_url}}',       sample: 'https://www.delhivery.com/track/DLV1234567890' },

  // Order Timeline (visual stepper) — backend pre-renders HTML based
  // on the order_status_raw value, so the merchant doesn't have to
  // hand-author per-status emails.
  order_timeline_html: { name: 'Order Timeline (HTML)', value: '{{order_timeline_html}}', sample: '' },

  // Sale countdown — automation flow fills this with a per-recipient
  // expiry timestamp (24h from open, end-of-sale, etc.).
  sale_ends_at:       { name: 'Sale Ends At',       value: '{{sale_ends_at}}',       sample: 'Feb 10, 2026 11:59 PM' },
  sale_promo_code:    { name: 'Promo Code',         value: '{{sale_promo_code}}',    sample: 'FLASH25' },
};

// Inline-CSS helpers — keep email-client compatibility.
const FRAME = 'border:1px solid #e5e7eb; border-radius:8px; padding:16px; margin:0 0 16px 0; font-family: -apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif; color:#111827; background:#ffffff;';
const HEADING = 'font-size:14px; font-weight:600; color:#6b7280; text-transform:uppercase; letter-spacing:0.5px; margin:0 0 8px 0;';
const ROW = 'font-size:14px; margin:6px 0; color:#111827;';
const MUTED = 'color:#6b7280;';

function btn(label, hrefTag, bg = '#111827') {
  return `<a href="${hrefTag}" style="display:inline-block; padding:10px 18px; background:${bg}; color:#ffffff; text-decoration:none; border-radius:6px; font-weight:600; font-size:14px;">${label}</a>`;
}

/**
 * Returns the list of custom Unlayer tool definitions. Pass each one
 * to `unlayer.registerTool(...)` in `onEditorLoad`.
 *
 * Each tool's `values` map exposes editor properties the user can
 * tweak (heading text, CTA label, accent color). The renderer reads
 * those values and stamps them into the email HTML.
 */
export function buildCustomBlocks() {
  return [
    // ────────────────────────────────────────────────────────────
    // 1. Abandoned Cart
    // ────────────────────────────────────────────────────────────
    {
      name: 'opx_abandoned_cart',
      label: 'Abandoned Cart',
      icon: 'fa-shopping-cart',
      // Drag-droppable in Body / Columns.
      supportedDisplayModes: ['web', 'email'],
      values: {
        heading: 'You left something behind',
        sub: 'Your cart is waiting — finish checkout in one click.',
        cta_label: 'Complete My Order',
        accent: '#dc2626',
      },
      options: {
        block: {
          title: 'Block',
          position: 1,
          options: {
            heading: { label: 'Heading', defaultValue: 'You left something behind', widget: 'text' },
            sub:     { label: 'Subheading', defaultValue: 'Your cart is waiting — finish checkout in one click.', widget: 'text' },
            cta_label: { label: 'CTA Label', defaultValue: 'Complete My Order', widget: 'text' },
            accent:    { label: 'Accent Color', defaultValue: '#dc2626', widget: 'color_picker' },
          },
        },
      },
      renderer: {
        Viewer: undefined,
        exporters: {
          web:   (v) => abandonedCartHtml(v),
          email: (v) => abandonedCartHtml(v),
        },
        head: { css: () => '', js: () => '' },
      },
    },

    // ────────────────────────────────────────────────────────────
    // 2. Store Credits Balance
    // ────────────────────────────────────────────────────────────
    {
      name: 'opx_store_credits',
      label: 'Store Credits',
      icon: 'fa-wallet',
      supportedDisplayModes: ['web', 'email'],
      values: {
        heading: 'Your Store Credit Balance',
        cta_label: 'Shop Now',
        accent: '#16a34a',
      },
      options: {
        block: {
          title: 'Block',
          position: 1,
          options: {
            heading:   { label: 'Heading',   defaultValue: 'Your Store Credit Balance', widget: 'text' },
            cta_label: { label: 'CTA Label', defaultValue: 'Shop Now', widget: 'text' },
            accent:    { label: 'Accent Color', defaultValue: '#16a34a', widget: 'color_picker' },
          },
        },
      },
      renderer: {
        exporters: {
          web:   (v) => storeCreditsHtml(v),
          email: (v) => storeCreditsHtml(v),
        },
        head: { css: () => '', js: () => '' },
      },
    },

    // ────────────────────────────────────────────────────────────
    // 3. Order Details
    // ────────────────────────────────────────────────────────────
    {
      name: 'opx_order_details',
      label: 'Order Details',
      icon: 'fa-receipt',
      supportedDisplayModes: ['web', 'email'],
      values: {
        heading: 'Order Details',
        show_items: true,
        show_totals: true,
        show_address: true,
      },
      options: {
        block: {
          title: 'Block',
          position: 1,
          options: {
            heading:      { label: 'Heading', defaultValue: 'Order Details', widget: 'text' },
            show_items:   { label: 'Show line items',    defaultValue: true, widget: 'toggle' },
            show_totals:  { label: 'Show totals',        defaultValue: true, widget: 'toggle' },
            show_address: { label: 'Show shipping address', defaultValue: true, widget: 'toggle' },
          },
        },
      },
      renderer: {
        exporters: {
          web:   (v) => orderDetailsHtml(v),
          email: (v) => orderDetailsHtml(v),
        },
        head: { css: () => '', js: () => '' },
      },
    },

    // ────────────────────────────────────────────────────────────
    // 4. Delivery Driver Details
    // ────────────────────────────────────────────────────────────
    {
      name: 'opx_driver_details',
      label: 'Driver Details',
      icon: 'fa-truck',
      supportedDisplayModes: ['web', 'email'],
      values: {
        heading: 'Your Delivery Driver',
        show_call_button: true,
      },
      options: {
        block: {
          title: 'Block',
          position: 1,
          options: {
            heading:          { label: 'Heading', defaultValue: 'Your Delivery Driver', widget: 'text' },
            show_call_button: { label: 'Show "Call Driver" button', defaultValue: true, widget: 'toggle' },
          },
        },
      },
      renderer: {
        exporters: {
          web:   (v) => driverDetailsHtml(v),
          email: (v) => driverDetailsHtml(v),
        },
        head: { css: () => '', js: () => '' },
      },
    },

    // ────────────────────────────────────────────────────────────
    // 5. Courier / AWB Tracking
    // ────────────────────────────────────────────────────────────
    {
      name: 'opx_courier_details',
      label: 'Courier Tracking',
      icon: 'fa-shipping-fast',
      supportedDisplayModes: ['web', 'email'],
      values: {
        heading: 'Tracking Info',
        cta_label: 'Track Package',
        accent: '#2563eb',
      },
      options: {
        block: {
          title: 'Block',
          position: 1,
          options: {
            heading:   { label: 'Heading',   defaultValue: 'Tracking Info', widget: 'text' },
            cta_label: { label: 'CTA Label', defaultValue: 'Track Package', widget: 'text' },
            accent:    { label: 'Accent Color', defaultValue: '#2563eb', widget: 'color_picker' },
          },
        },
      },
      renderer: {
        exporters: {
          web:   (v) => courierDetailsHtml(v),
          email: (v) => courierDetailsHtml(v),
        },
        head: { css: () => '', js: () => '' },
      },
    },

    // ────────────────────────────────────────────────────────────
    // 6. Order Timeline — visual stepper Placed → Packed → Shipped …
    //
    // Pure-HTML stepper that reads the live order status from the
    // same `order_status_raw` merge tag the Order Details block uses,
    // so a single trigger fills both blocks without extra wiring.
    // The {{order_timeline_html}} placeholder is resolved by
    // email_merge_tags.py on the server.
    // ────────────────────────────────────────────────────────────
    {
      name: 'opx_order_timeline',
      label: 'Order Timeline',
      icon: 'fa-tasks',
      supportedDisplayModes: ['web', 'email'],
      values: {
        heading: 'Order Progress',
      },
      options: {
        block: {
          title: 'Block',
          position: 1,
          options: {
            heading: { label: 'Heading', defaultValue: 'Order Progress', widget: 'text' },
          },
        },
      },
      renderer: {
        exporters: {
          web:   (v) => orderTimelineHtml(v),
          email: (v) => orderTimelineHtml(v),
        },
        head: { css: () => '', js: () => '' },
      },
    },

    // ────────────────────────────────────────────────────────────
    // 7. Sale Countdown Timer — Unlayer has a generic Timer tool but
    // it requires the user to set a date by hand every time. This
    // wrapper points at a merge tag (`{{sale_ends_at}}`) so the
    // automation flow can pre-configure expiry per recipient (24h
    // from email open, end-of-day, etc.) and the block re-uses the
    // same template indefinitely.
    // ────────────────────────────────────────────────────────────
    {
      name: 'opx_sale_countdown',
      label: 'Sale Countdown',
      icon: 'fa-hourglass-half',
      supportedDisplayModes: ['web', 'email'],
      values: {
        heading: 'Offer ends soon!',
        sub: 'Grab it before the timer runs out.',
        accent: '#f59e0b',
        cta_label: 'Shop The Sale',
      },
      options: {
        block: {
          title: 'Block',
          position: 1,
          options: {
            heading:   { label: 'Heading',   defaultValue: 'Offer ends soon!', widget: 'text' },
            sub:       { label: 'Subheading', defaultValue: 'Grab it before the timer runs out.', widget: 'text' },
            cta_label: { label: 'CTA Label', defaultValue: 'Shop The Sale', widget: 'text' },
            accent:    { label: 'Accent Color', defaultValue: '#f59e0b', widget: 'color_picker' },
          },
        },
      },
      renderer: {
        exporters: {
          web:   (v) => saleCountdownHtml(v),
          email: (v) => saleCountdownHtml(v),
        },
        head: { css: () => '', js: () => '' },
      },
    },
  ];
}

// ───────── Renderers ─────────

function abandonedCartHtml(v) {
  return `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${FRAME} border-left:4px solid ${v.accent || '#dc2626'};">
  <tr><td>
    <p style="${HEADING}">Abandoned Cart</p>
    <h2 style="margin:0 0 6px 0; font-size:20px; color:#111827;">${escapeHtml(v.heading || 'You left something behind')}</h2>
    <p style="${ROW} ${MUTED}">${escapeHtml(v.sub || '')}</p>
    <ul style="margin:12px 0; padding-left:20px; font-size:14px; color:#111827;">{{cart_items_html}}</ul>
    <p style="${ROW}"><strong>{{cart_items_count}} item(s) • Total: {{cart_total}}</strong></p>
    <p style="margin-top:16px;">${btn(escapeHtml(v.cta_label || 'Complete My Order'), '{{cart_url}}', v.accent || '#dc2626')}</p>
  </td></tr>
</table>`;
}

function storeCreditsHtml(v) {
  return `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${FRAME} background:#f0fdf4; border:1px solid #bbf7d0;">
  <tr><td>
    <p style="${HEADING}">Store Credit</p>
    <h2 style="margin:0 0 8px 0; font-size:20px; color:#065f46;">${escapeHtml(v.heading || 'Your Store Credit Balance')}</h2>
    <p style="font-size:32px; font-weight:700; margin:6px 0 4px 0; color:#16a34a;">{{store_credit_balance}}</p>
    <p style="${ROW} ${MUTED}">Expires: {{store_credit_expiry}}</p>
    <p style="margin-top:14px;">${btn(escapeHtml(v.cta_label || 'Shop Now'), '#', v.accent || '#16a34a')}</p>
  </td></tr>
</table>`;
}

function orderDetailsHtml(v) {
  const items = v.show_items !== false ? `<ul style="margin:8px 0 12px 0; padding-left:20px; font-size:14px;">{{order_items_html}}</ul>` : '';
  const totals = v.show_totals !== false ? `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:8px; font-size:14px;">
      <tr><td style="padding:4px 0; ${MUTED}">Subtotal</td><td style="text-align:right;">{{order_subtotal}}</td></tr>
      <tr><td style="padding:4px 0; ${MUTED}">Shipping</td><td style="text-align:right;">{{order_shipping}}</td></tr>
      <tr><td style="padding:4px 0; ${MUTED}">Tax</td><td style="text-align:right;">{{order_tax}}</td></tr>
      <tr><td style="padding:8px 0; font-weight:700; border-top:1px solid #e5e7eb;">Total</td>
          <td style="padding:8px 0; text-align:right; font-weight:700; border-top:1px solid #e5e7eb;">{{order_total}}</td></tr>
    </table>` : '';
  const addr = v.show_address !== false ? `<p style="${ROW} ${MUTED} margin-top:12px;"><strong>Ship to:</strong> {{shipping_address}}</p>` : '';
  return `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${FRAME}">
  <tr><td>
    <p style="${HEADING}">${escapeHtml(v.heading || 'Order Details')}</p>
    <p style="${ROW}"><strong>Order {{order_number}}</strong> • {{order_date}} • Status: <strong>{{order_status}}</strong></p>
    ${items}
    ${totals}
    ${addr}
  </td></tr>
</table>`;
}

function driverDetailsHtml(v) {
  const callBtn = v.show_call_button !== false
    ? `<p style="margin-top:14px;">${btn('Call Driver', 'tel:{{driver_phone}}', '#0ea5e9')}</p>`
    : '';
  return `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${FRAME} background:#eff6ff; border:1px solid #bfdbfe;">
  <tr><td>
    <p style="${HEADING}">${escapeHtml(v.heading || 'Your Delivery Driver')}</p>
    <p style="${ROW}"><strong>{{driver_name}}</strong></p>
    <p style="${ROW} ${MUTED}">Phone: <a href="tel:{{driver_phone}}" style="color:#2563eb; text-decoration:none;">{{driver_phone}}</a></p>
    <p style="${ROW} ${MUTED}">Vehicle: {{driver_vehicle}}</p>
    <p style="${ROW} ${MUTED}">ETA: <strong>{{driver_eta}}</strong></p>
    ${callBtn}
  </td></tr>
</table>`;
}

function courierDetailsHtml(v) {
  return `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${FRAME} border-left:4px solid ${v.accent || '#2563eb'};">
  <tr><td>
    <p style="${HEADING}">${escapeHtml(v.heading || 'Tracking Info')}</p>
    <p style="${ROW}"><strong>{{courier_name}}</strong> • AWB: <code>{{awb_number}}</code></p>
    <p style="${ROW} ${MUTED}">Expected delivery: <strong>{{courier_eta}}</strong></p>
    <p style="margin-top:14px;">${btn(escapeHtml(v.cta_label || 'Track Package'), '{{tracking_url}}', v.accent || '#2563eb')}</p>
  </td></tr>
</table>`;
}

function orderTimelineHtml(v) {
  // The actual stepper HTML is pre-rendered server-side from the live
  // order status (see email_merge_tags._timeline_html). The block
  // renders a heading + a placeholder div that the backend replaces.
  return `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 8px 0;">
  <tr><td>
    <p style="${HEADING}">${escapeHtml(v.heading || 'Order Progress')}</p>
    {{order_timeline_html}}
  </td></tr>
</table>`;
}

function saleCountdownHtml(v) {
  const accent = v.accent || '#f59e0b';
  return `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${FRAME} text-align:center; background:#fffbeb; border:1px solid #fde68a;">
  <tr><td align="center">
    <p style="${HEADING} text-align:center;">Limited Time Offer</p>
    <h2 style="margin:0 0 6px 0; font-size:22px; color:#92400e;">${escapeHtml(v.heading || 'Offer ends soon!')}</h2>
    <p style="${ROW} ${MUTED} text-align:center;">${escapeHtml(v.sub || '')}</p>
    <p style="font-size:18px; font-weight:700; color:${accent}; margin:12px 0 6px 0;">
      Ends: {{sale_ends_at}}
    </p>
    <p style="font-size:13px; color:#6b7280; margin:0 0 14px 0;">
      Use code: <code style="background:#fef3c7; padding:2px 8px; border-radius:4px; font-size:14px; color:#92400e;">{{sale_promo_code}}</code>
    </p>
    <p>${btn(escapeHtml(v.cta_label || 'Shop The Sale'), '#', accent)}</p>
  </td></tr>
</table>`;
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
